import { useEffect, useState } from "react";
import api, { errMsg, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import PersonnelMap from "@/components/app/PersonnelMap";
import { LocationPickerModal } from "@/components/app/LocationPickerModal";
import { Plus, Pencil, Trash2, X, Upload, Search, FileSpreadsheet, Download, MapPin, List } from "lucide-react";
import { toast } from "sonner";
import { hasGoogle, googleGeocode, bestGeocodeResult } from "@/lib/googleMaps";

const emptyForm = () => ({
  first_name: "", last_name: "", phone: "",
  company_id: "", company_name: "",
  address: "", district: "", city: "",
  pickup_time: "", shift: "", notes: "",
});

export default function CustomerPersonnelPage() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [xlsxCompany, setXlsxCompany] = useState("");
  const [view, setView] = useState("list"); // list | map
  const [geocoding, setGeocoding] = useState(false);
  const [locPerson, setLocPerson] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [geoProgress, setGeoProgress] = useState(null); // Google toplu konumlama ilerlemesi

  const load = async () => {
    try {
      const [p, c] = await Promise.all([api.get("/customer-personnel"), api.get("/companies")]);
      setItems(p.data); setCompanies(c.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      first_name: r.first_name || "", last_name: r.last_name || "", phone: r.phone || "",
      company_id: r.company_id || "", company_name: r.company_name || "",
      address: r.address || "", district: r.district || "", city: r.city || "",
      pickup_time: r.pickup_time || "", shift: r.shift || "", notes: r.notes || "",
      lat: typeof r.lat === "number" ? r.lat : null, lng: typeof r.lng === "number" ? r.lng : null,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    // if a company is selected via select, set name from list
    let payload = { ...form };
    if (!payload.company_name && payload.company_id) {
      const c = companies.find(x => x.id === payload.company_id);
      if (c) payload.company_name = c.name;
    }
    if (!payload.company_name) { toast.error("Firma adı gerekli"); return; }
    try {
      if (editing) await api.put(`/customer-personnel/${editing.id}`, payload);
      else await api.post("/customer-personnel", payload);
      toast.success("Kaydedildi");
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`${r.first_name} ${r.last_name} silinsin mi?`)) return;
    try { await api.delete(`/customer-personnel/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const bulkDelete = async (ids, label) => {
    if (ids.length === 0) { toast.info("Silinecek kayıt yok"); return; }
    if (!window.confirm(`${label} — ${ids.length} kayıt kalıcı olarak silinecek. Emin misiniz?`)) return;
    try {
      const { data } = await api.post("/customer-personnel/bulk-delete", { ids });
      toast.success(`${data.deleted} kayıt silindi`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const bulkChangeCompany = async (ids, company) => {
    if (!window.confirm(`${ids.length} personelin firması "${company}" olarak değiştirilsin mi?`)) throw new Error("iptal");
    try {
      const { data } = await api.post("/customer-personnel/bulk-company", { personnel_ids: ids, company_name: company });
      toast.success(`${data.updated} personelin firması "${data.company_name}" olarak güncellendi`);
      load();
    } catch (err) { toast.error(errMsg(err)); throw err; }
  };

  const toggleSel = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const submitBulk = async () => {
    if (!bulkCompany.trim()) { toast.error("Firma adı gerekli"); return; }
    // Parse text — each line: Ad,Soyad,Telefon,İlçe,Adres,Vardiya,Alım Saati
    const rows = bulkText.split("\n").map(l => l.trim()).filter(Boolean).map(line => {
      const cols = line.split(/[,;\t]/).map(x => x.trim());
      return {
        first_name: cols[0] || "", last_name: cols[1] || "", phone: cols[2] || "",
        district: cols[3] || "", address: cols[4] || "", shift: cols[5] || "", pickup_time: cols[6] || "",
      };
    });
    if (!rows.length) { toast.error("Geçerli satır bulunamadı"); return; }
    try {
      const { data } = await api.post("/customer-personnel/bulk", { company_name: bulkCompany.trim(), rows });
      toast.success(`${data.inserted} personel eklendi`);
      setShowBulk(false); setBulkText(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const filtered = items.filter(r => {
    const t = filter.toLowerCase();
    const matchText = !t || `${r.first_name} ${r.last_name} ${r.phone} ${r.address} ${r.district} ${r.city}`.toLowerCase().includes(t);
    const matchCompany = !companyFilter || r.company_name === companyFilter;
    return matchText && matchCompany;
  });

  const companyList = [...new Set(items.map(x => x.company_name))].filter(Boolean).sort();
  const withCoords = items.filter(p => typeof p.lat === "number" && typeof p.lng === "number");
  const withoutCoords = items.filter(p => !(typeof p.lat === "number" && typeof p.lng === "number"));
  const filteredUnlocated = filtered.filter(p => !(typeof p.lat === "number" && typeof p.lng === "number"));
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.id));
  const toggleAll = () => setSelected(allFilteredSelected ? new Set() : new Set(filtered.map(r => r.id)));

  const downloadTemplate = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/customer-personnel/template/xlsx?auth=${token || ""}`, "_blank");
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    const company = xlsxCompany || window.prompt("Bu personellerin çalıştığı firma adı:");
    if (!company) { toast.error("Firma adı gerekli"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_name", company);
    try {
      const { data } = await api.post("/customer-personnel/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} personel yüklendi${data.skipped ? ` (${data.skipped} atlandı)` : ""}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const runOsmBatch = async () => {
    try {
      const batchSize = 20;
      const ids = withoutCoords.slice(0, batchSize).map(p => p.id);
      const { data } = await api.post("/customer-personnel/geocode-batch", { ids, limit: batchSize });
      const ok = data.results.filter(r => r.status === "ok").length;
      const nf = data.results.filter(r => r.status === "not_found").length;
      toast.success(`${ok} adres konumlandı${nf ? ` · ${nf} adres bulunamadı` : ""}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setGeocoding(false); }
  };

  const runGeocode = async () => {
    if (withoutCoords.length === 0) { toast.info("Koordinatı olmayan personel yok"); return; }
    setGeocoding(true);
    // Google Maps ile (sokak seviyesi hassasiyet) — anahtar varsa tercih edilir
    if (hasGoogle()) {
      let ok = 0, nf = 0;
      const total = withoutCoords.length;
      try {
        for (let i = 0; i < withoutCoords.length; i++) {
          const p = withoutCoords[i];
          const q = [p.address, p.district, p.city].filter(Boolean).join(", ");
          if (!q.trim()) { nf++; setGeoProgress({ done: i + 1, total, ok, nf }); continue; }
          try {
            const res = await googleGeocode(q);
            const best = bestGeocodeResult(res);
            if (best) {
              await api.put(`/customer-personnel/${p.id}/location`, { lat: best.lat, lng: best.lng });
              ok++;
            } else nf++;
          } catch (e) {
            if (String(e.message || e).startsWith("GOOGLE_AUTH") || String(e.message || e).includes("yetkili değil")) throw e; // anahtar sorunu — tümünü durdur
            nf++;
          }
          setGeoProgress({ done: i + 1, total, ok, nf });
        }
        toast.success(`Google ile ${ok} adres konumlandı${nf ? ` · ${nf} adres bulunamadı (Konum Düzelt ile elle işaretleyin)` : ""}`, { duration: 7000 });
        load();
        setGeocoding(false);
        setGeoProgress(null);
      } catch (e) {
        setGeoProgress(null);
        toast.warning(`Google anahtarı bu alan adı için yetkili değil — ücretsiz OSM ile devam ediliyor. Kalıcı çözüm: Google Cloud Console → Credentials → anahtarınız → Website restrictions listesine bu siteyi ekleyin.`, { duration: 12000 });
        if (ok > 0) load();
        await runOsmBatch();
      }
      return;
    }
    // Anahtar yoksa: ücretsiz OSM toplu konumlama (20'şerli)
    await runOsmBatch();
  };

  return (
    <>
      <PageHeader
        title="Taşınan Personel"
        subtitle="Firmaların çalışanlarını isim ve adresleriyle kaydedin — sistem güzergah oluştururken bu adresleri kullanır. Excel ile toplu yükleyip haritada gösterebilirsiniz."
        testid="customer-personnel-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="cp-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Download className="w-3.5 h-3.5" /> Boş Excel
            </button>
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <FileSpreadsheet className="w-3.5 h-3.5" /> <span data-testid="cp-xlsx-upload">Excel Yükle</span>
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button data-testid="bulk-import-btn" onClick={() => { setBulkCompany(""); setBulkText(""); setShowBulk(true); }} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Upload className="w-3.5 h-3.5" /> Yapıştır
            </button>
            <button data-testid="cp-add-btn" onClick={openCreate} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Personel
            </button>
          </div>
        }
      />

      {/* View toggle + geocode */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-white border border-[#E5E5E5] p-1">
          <button
            data-testid="view-list-btn"
            onClick={() => setView("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${view === "list" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F5F5F5]"}`}
          >
            <List className="w-3.5 h-3.5" /> Liste ({items.length})
          </button>
          <button
            data-testid="view-map-btn"
            onClick={() => setView("map")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${view === "map" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F5F5F5]"}`}
          >
            <MapPin className="w-3.5 h-3.5" /> Harita ({withCoords.length})
          </button>
        </div>

        {items.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="text-xs text-neutral-500">
              <span className="num font-semibold text-[#16A34A]">{withCoords.length}</span> konumlanmış / <span className="num font-semibold text-[#F59E0B]">{withoutCoords.length}</span> bekliyor
            </div>
            <button
              data-testid="geocode-btn"
              onClick={runGeocode}
              disabled={geocoding || withoutCoords.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-[0.15em] bg-[#0020C2] text-white hover:bg-[#0A0A0A] transition-colors disabled:opacity-50"
            >
              <MapPin className="w-3.5 h-3.5" />
              {geocoding
                ? (geoProgress ? `Google: ${geoProgress.done}/${geoProgress.total} (${geoProgress.ok} bulundu)` : "İşleniyor...")
                : hasGoogle()
                  ? `Adresleri Google ile Konumla (${withoutCoords.length})`
                  : `Adresleri Haritada Konumla (${Math.min(withoutCoords.length, 20)})`}
            </button>
          </div>
        )}
      </div>

      {view === "map" && (
        <div className="mb-4">
          <PersonnelMap personnel={filtered} height={560} onFix={setLocPerson} onDelete={remove} onBulkCompany={bulkChangeCompany} />
        </div>
      )}

      {view === "list" && <>
      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 mb-4 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Search className="w-4 h-4 text-neutral-400" />
          <input data-testid="cp-search" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Ad, adres, ilçe ara..." className="flex-1 border-0 bg-transparent focus:outline-none text-sm" />
        </div>
        <select data-testid="cp-company-filter" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)} className="px-3 py-1.5 border border-[#E5E5E5] text-sm bg-white">
          <option value="">Tüm Firmalar</option>
          {companyList.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-xs text-neutral-500 num">{filtered.length} kayıt</div>
      </div>

      {/* Toplu silme araç çubuğu */}
      {filtered.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap" data-testid="cp-bulk-toolbar">
          <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Toplu Silme:</span>
          <button
            data-testid="cp-bulk-delete-selected"
            onClick={() => bulkDelete([...selected], "Seçilen personeller")}
            disabled={selected.size === 0}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#DC2626] text-white hover:bg-[#0A0A0A] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3 h-3" /> Seçilenleri Sil ({selected.size})
          </button>
          <button
            data-testid="cp-bulk-delete-unlocated"
            onClick={() => bulkDelete(filteredUnlocated.map(p => p.id), "Konum onayı olmayan (konumlanmamış) personeller")}
            disabled={filteredUnlocated.length === 0}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#F59E0B] text-[#B45309] hover:bg-[#F59E0B] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <MapPin className="w-3 h-3" /> Konumu Olmayanları Sil ({filteredUnlocated.length})
          </button>
          <button
            data-testid="cp-bulk-delete-filtered"
            onClick={() => bulkDelete(filtered.map(p => p.id), companyFilter ? `"${companyFilter}" firmasındaki filtrelenen tüm personeller` : "Filtredeki TÜM personeller")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
          >
            <Trash2 className="w-3 h-3" /> Filtredekilerin Tümünü Sil ({filtered.length})
          </button>
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())} className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 hover:underline ml-auto">Seçimi temizle</button>
          )}
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-neutral-500 text-sm mb-3">Kayıt bulunamadı.</div>
            <button onClick={openCreate} className="text-xs uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">İlk personeli ekle →</button>
          </div>
        ) : (
          <div className="overflow-x-auto data-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  <th className="px-4 py-3 w-8">
                    <input type="checkbox" data-testid="cp-select-all" checked={allFilteredSelected} onChange={toggleAll} className="cursor-pointer" title="Tümünü seç" />
                  </th>
                  {["Ad Soyad","Firma","İlçe","Adres","Konum","Vardiya","Alım","Telefon",""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} className={`border-b border-[#F5F5F5] ${selected.has(r.id) ? "bg-[#FEF2F2]" : ""}`} data-testid={`cp-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" data-testid={`cp-select-${r.id}`} checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} className="cursor-pointer" />
                    </td>
                    <td className="px-4 py-3 font-medium">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-3 text-neutral-600">{r.company_name}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 bg-[#F5F5F5]">{r.district || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 max-w-xs truncate">{r.address}</td>
                    <td className="px-4 py-3">
                      {typeof r.lat === "number"
                        ? <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#DCFCE7] text-[#15803D]">Konumlu</span>
                        : <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FEF3C7] text-[#B45309]">Konumsuz</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">{r.shift || "-"}</td>
                    <td className="px-4 py-3 num text-xs">{r.pickup_time || "-"}</td>
                    <td className="px-4 py-3 num text-xs">{r.phone || "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`cp-locate-${r.id}`}
                          title={typeof r.lat === "number" ? "Konumu düzelt (adresle bul)" : "Konum ata (adresle bul)"}
                          onClick={() => setLocPerson(r)}
                          className={`p-1.5 hover:bg-[#F5F5F5] ${typeof r.lat === "number" ? "text-[#16A34A]" : "text-[#F59E0B]"}`}
                        >
                          <MapPin className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`cp-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`cp-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}

      {/* Single form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Taşınan Personel</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ad *</label>
                <input data-testid="cp-field-first" required value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Soyad *</label>
                <input data-testid="cp-field-last" required value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma *</label>
                <select data-testid="cp-field-company" required value={form.company_id || form.company_name} onChange={e => {
                  const v = e.target.value;
                  const c = companies.find(x => x.id === v);
                  setForm({...form, company_id: c ? c.id : "", company_name: c ? c.name : v});
                }} className="input mt-1.5">
                  <option value="">Seçiniz</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  {form.company_name && !companies.find(c => c.name === form.company_name) && (
                    <option value={form.company_name}>{form.company_name}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Telefon</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Alım Saati</label>
                <input placeholder="07:30" value={form.pickup_time} onChange={e => setForm({...form, pickup_time: e.target.value})} className="input mt-1.5 num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İlçe *</label>
                <input data-testid="cp-field-district" required value={form.district} onChange={e => setForm({...form, district: e.target.value})} placeholder="Etimesgut" className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şehir</label>
                <input value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Ankara" className="input mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adres</label>
                <textarea rows={2} value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="input mt-1.5" placeholder="Mahalle, sokak, kapı no..." />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vardiya</label>
                <input placeholder="Sabah/Akşam/Gece" value={form.shift} onChange={e => setForm({...form, shift: e.target.value})} className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not</label>
                <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input mt-1.5" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="cp-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk import modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktar</div>
                <div className="font-heading font-bold text-lg">Personel Listesi Yapıştır</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma Adı *</label>
                <input data-testid="bulk-company" required value={bulkCompany} onChange={e => setBulkCompany(e.target.value)} placeholder="Bu personellerin çalıştığı firma" className="input mt-1.5" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Personel Listesi</label>
                <div className="text-[11px] text-neutral-500 mt-1 mb-2">
                  Her satıra bir personel — sütun sırası: <span className="num font-semibold">Ad, Soyad, Telefon, İlçe, Adres, Vardiya, Alım Saati</span> (virgül veya sekme ile ayrılmış)
                </div>
                <textarea
                  data-testid="bulk-text"
                  rows={10}
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                  placeholder="Ali, Yılmaz, 05551112233, Etimesgut, Yaşar Doğu Mah. Ok Sok. No:12, Sabah, 07:00
Ayşe, Demir, 05551112244, Etimesgut, Şeker Mah. 3. Cad. No:5, Sabah, 07:00"
                  className="input mt-1 font-mono text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="bulk-submit" onClick={submitBulk} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Toplu Ekle</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Konum düzeltme modalı */}
      {locPerson && (
        <LocationPickerModal person={locPerson} onClose={() => setLocPerson(null)} onSaved={load} />
      )}
    </>
  );
}
